import { AgentClient } from "@/agent/client/class";
import type { AgentClientDefinition, AgentClientFromBuild } from "@/agent/client/types";
import { type ClientBuild, importClientBuild } from "@/exports/build/client";
import * as op from "@/shared/operation";
import type { TelemetryClient } from "@/telemetry/clients/base";
import { createTelemetryClient, TelemetryBrowserClient } from "@/telemetry/clients/browser";
import { formatLogForBrowser } from "@/telemetry/helpers/formatting/browser";
import { logLevelPriority } from "@/telemetry/helpers/log-level-priority";
import type { TelemetryLogLevel } from "@/telemetry/types";
import { LifeServerApiClient } from "./api";
import type { LifeClientOptions } from "./options";

// Stream formatted telemetry logs to the terminal
TelemetryBrowserClient.registerGlobalConsumer({
  async start(queue) {
    for await (const item of queue) {
      if (item.type !== "log") continue;
      const logLevel = (globalThis?.process?.env?.LOG_LEVEL as TelemetryLogLevel) ?? "info";

      // Ignore logs lower than the requested log level
      const priority = logLevelPriority(item.level);
      if (priority < logLevelPriority(logLevel as TelemetryLogLevel)) continue;

      // Format and print the log
      try {
        const contents = (await formatLogForBrowser(item)).filter(Boolean);
        let consoleFn: (line: string) => void;
        if (priority >= logLevelPriority("error")) consoleFn = console.error;
        else if (priority >= logLevelPriority("warn")) consoleFn = console.warn;
        else consoleFn = console.log;
        for (let i = 0; i < contents.length; i++)
          consoleFn(
            `Life.js (${item.id.slice(0, 6)}, ${i + 1}/${contents.length})\n${contents[i]}`,
          );
      } catch {
        console.log(item.message);
      }
    }
  },
});

export class LifeClient {
  readonly options: LifeClientOptions;
  readonly #agents = new Map<string, AgentClient<AgentClientDefinition>>();
  readonly #telemetry: TelemetryClient;
  api: LifeServerApiClient;

  constructor(options: LifeClientOptions) {
    this.options = options;

    // Initialize telemetry
    this.#telemetry = createTelemetryClient("client", {});

    // Initialize API client
    this.api = new LifeServerApiClient({
      telemetry: this.#telemetry,
      serverUrl: this.options.serverUrl,
      serverToken: this.options.serverToken,
    });
  }

  /**
   * Create a new agent instance on the server
   * @param name - Agent name/type to create
   * @param scope - Agent scope configuration
   * @returns AgentClient instance if creation successful
   */
  async createAgent<Name extends keyof ClientBuild>(name: Name, options: { id?: string } = {}) {
    return await this.#telemetry.trace("createAgent()", async (span) => {
      const [error, agent] = await this.#createAgent(name, options);
      if (error) {
        span.log.error({ error });
        return op.failure(error);
      }
      return op.success(agent);
    });
  }

  // Private method, doesn't log to telemetry
  async #createAgent<Name extends keyof ClientBuild>(name: Name, options: { id?: string } = {}) {
    return await this.#telemetry.trace("#createAgent()", async () => {
      try {
        // Load the client build if not already loaded
        const [errIndex, buildIndex] = await importClientBuild();
        if (errIndex) return op.failure(errIndex);
        const build = buildIndex[name as keyof ClientBuild];
        if (!build) {
          return op.failure({
            code: "NotFound",
            message: `Agent '${String(name)}' not found in client build.`,
          });
        }

        // Send a call to the server to create the agent
        const [err, data] = await this.api.call("agent.create", { name, id: options.id });
        if (err) return op.failure(err);

        // Create agent client with proper definition and plugins from build
        const agentClient = new AgentClient({
          id: data.id,
          definition: build.definition,
          life: this,
          config: data.clientConfig ?? {},
        });
        this.#agents.set(data.id, agentClient);

        // Return the agent client
        return op.success(op.toPublic(agentClient) as AgentClientFromBuild<Name>);
      } catch (error) {
        return op.failure({
          code: "Unknown",
          message: "Unknown error while creating agent.",
          cause: error,
        });
      }
    });
  }

  /**
   * Get an existing agent client instance
   * @param id - Agent ID
   * @returns AgentClient instance or undefined
   */
  getAgent<Name extends keyof ClientBuild>(name: Name, options: { id?: string } = {}) {
    return this.#telemetry.trace("getAgent()", (span) => {
      const [error, agent] = this.#getAgent<Name>(name, options);
      if (error) {
        span.log.error({ error });
        return op.failure(error);
      }
      return op.success(agent);
    });
  }

  // Private method, doesn't log to telemetry
  #getAgent<Name extends keyof ClientBuild>(name: Name, options: { id?: string } = {}) {
    return this.#telemetry.trace("#getAgent()", () => {
      try {
        // If ID is provided, get the agent from the map
        if (options.id) {
          const agent = this.#agents.get(options.id);
          if (!agent) return op.success(undefined);
          return op.success(op.toPublic(agent) as unknown as AgentClientFromBuild<Name>);
        }

        // Otherwise, retrieve the agent by name
        const agents = Array.from(this.#agents.values()).filter((a) => a.def.name === String(name));
        if (!agents.length) return op.success(undefined);

        // If many agents exist for this same name, return failure
        if (agents.length > 1)
          return op.failure({
            code: "Conflict",
            message: `Multiple agents found for name '${String(name)}'. Use an ID to get a specific agent.`,
          });

        // Else, return the first agent
        return op.success(op.toPublic(agents[0]) as unknown as AgentClientFromBuild<Name>);
      } catch (error) {
        return op.failure({
          code: "Unknown",
          message: "Unknown error while getting agent.",
          cause: error,
        });
      }
    });
  }

  /**
   * Get or create an agent instance
   * @param name - Agent name/type
   * @param scope - Agent scope configuration
   * @returns AgentClient instance
   */
  async getOrCreateAgent<Name extends keyof ClientBuild>(
    name: Name,
    options: { id?: string } = {},
  ) {
    return await this.#telemetry.trace("getOrCreateAgent()", async (span) => {
      const [error, agent] = await this.#getOrCreateAgent(name, options);
      if (error) {
        span.log.error({ error });
        return op.failure(error);
      }
      return op.success(agent);
    });
  }

  // Private method, doesn't log to telemetry
  async #getOrCreateAgent<Name extends keyof ClientBuild>(
    name: Name,
    options: { id?: string } = {},
  ) {
    return await this.#telemetry.trace("#getOrCreateAgent()", async () => {
      try {
        // Try to get the agent if it already exists
        const [error, agent] = this.getAgent(name, options);
        if (error && error.code !== "Conflict") return op.failure(error);
        if (agent) return op.success(agent);

        // Else, create a new agent
        const [err, newAgent] = await this.#createAgent(name, options);
        if (err) return op.failure(err);
        return op.success(newAgent);
      } catch (error) {
        return op.failure({
          code: "Unknown",
          message: "Unknown error while getting or creating agent.",
          cause: error,
        });
      }
    });
  }

  /**
   * List all created agent instances
   * @returns Array of { name: string, id: string }
   */
  listAgents() {
    return this.#telemetry.trace("listAgents()", (span) => {
      const [error, agents] = this.#listAgents();
      if (error) {
        span.log.error({ error });
        return op.failure(error);
      }
      return op.success(agents);
    });
  }

  // Private method, doesn't log to telemetry
  #listAgents() {
    return this.#telemetry.trace("#listAgents()", () => {
      try {
        return op.success(
          Array.from(this.#agents.values()).map((a) => ({ name: a.name, id: a.id })),
        );
      } catch (error) {
        return op.failure({
          code: "Unknown",
          message: "Unknown error while listing agents.",
          cause: error,
        });
      }
    });
  }

  /**
   * Get server information
   * @returns Server info response
   */
  async info() {
    return await this.#telemetry.trace("info()", async (span) => {
      const [error, data] = await this.#info();
      if (error) {
        span.log.error({ error });
        return op.failure(error);
      }
      return op.success(data);
    });
  }

  // Private method, doesn't log to telemetry
  async #info() {
    return await this.#telemetry.trace("info()", async () => {
      try {
        // Send a call to the server to get server information
        const [err, data] = await this.api.call("server.info");
        if (err) return op.failure(err);
        return op.success(data);
      } catch (error) {
        return op.failure({
          code: "Unknown",
          message: "Unknown error while getting server info.",
          cause: error,
        });
      }
    });
  }

  /**
   * Check if the server is responsive
   * @returns True if server responds with "pong"
   */
  async ping() {
    return await this.#telemetry.trace("ping()", async (span) => {
      const [error, data] = await this.#ping();
      if (error) {
        span.log.error({ error });
        return op.failure(error);
      }
      return op.success(data);
    });
  }

  // Private method, doesn't log to telemetry
  async #ping() {
    return await this.#telemetry.trace("#ping()", async () => {
      try {
        const [err, data] = await this.api.call("server.ping");
        if (err) return op.failure(err);
        if (data !== "pong")
          return op.failure({
            code: "Validation",
            message: `Ping failed. Received wrong response: '${data}'.`,
          });
        return op.success("pong");
      } catch (error) {
        return op.failure({
          code: "Unknown",
          message: "Unknown error while pinging server.",
          cause: error,
        });
      }
    });
  }
}
